import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Tab,
  Tabs,
} from '@mui/material';
import {
  CheckCircle,
  Flag,
  Refresh,
  Close,
  Send,
  ThumbUp,
  Warning,
  AccountBalance,
  CompareArrows,
  VerifiedUser,
  Summarize,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiGet } from '../services/apiClient';
import {
  depositaryService,
  DepositaryCashMonitor,
  DepositarySafekeeping,
  DepositaryNavVerification,
  DepositaryReport,
} from '../services/depositaryService';
import { LoggerService } from '../services/LoggerService';

interface Fund {
  id: string;
  name: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const DepositaryPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [funds, setFunds] = useState<Fund[]>([]);

  // Cash state
  const [cashItems, setCashItems] = useState<DepositaryCashMonitor[]>([]);
  const [cashStatusFilter, setCashStatusFilter] = useState('');
  const [cashFundFilter, setCashFundFilter] = useState('');
  const [cashLoading, setCashLoading] = useState(false);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagTarget, setFlagTarget] = useState<string | null>(null);
  const [flagReason, setFlagReason] = useState('');

  // Safekeeping state
  const [safekeepingItems, setSafekeepingItems] = useState<DepositarySafekeeping[]>([]);
  const [safekeepingLoading, setSafekeepingLoading] = useState(false);
  const [reconFundId, setReconFundId] = useState('');
  const [reconDate, setReconDate] = useState(new Date().toISOString().split('T')[0]);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');

  // NAV Verification state
  const [navItems, setNavItems] = useState<DepositaryNavVerification[]>([]);
  const [navLoading, setNavLoading] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<string | null>(null);
  const [depositaryNav, setDepositaryNav] = useState('');
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeTarget, setDisputeTarget] = useState<string | null>(null);
  const [disputeReason, setDisputeReason] = useState('');

  // Reports state
  const [reports, setReports] = useState<DepositaryReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [genReportFundId, setGenReportFundId] = useState('');
  const [genReportType, setGenReportType] = useState('');
  const [genReportPeriod, setGenReportPeriod] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadFunds = useCallback(async () => {
    try {
      const response = await apiGet<{ funds: Fund[] }>('/funds/');
      setFunds(response.funds || []);
    } catch (err: any) {
      LoggerService.error('Failed to load funds', err);
      setError(err.message || 'Failed to load funds');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadFunds();
      setLoading(false);
    };
    init();
  }, [loadFunds]);

  // Cash tab data loading
  const loadCashItems = useCallback(async () => {
    setCashLoading(true);
    try {
      const data = await depositaryService.listCash(
        cashFundFilter || undefined,
        cashStatusFilter || undefined,
      );
      setCashItems(data);
    } catch (err: any) {
      LoggerService.error('Failed to load cash items', err);
      enqueueSnackbar('Failed to load cash monitoring data', { variant: 'error' });
    } finally {
      setCashLoading(false);
    }
  }, [cashFundFilter, cashStatusFilter, enqueueSnackbar]);

  useEffect(() => {
    if (tabIndex === 0) loadCashItems();
  }, [tabIndex, loadCashItems]);

  // Safekeeping data loading
  const loadSafekeeping = useCallback(async () => {
    setSafekeepingLoading(true);
    try {
      const data = await depositaryService.listSafekeeping();
      setSafekeepingItems(data);
    } catch (err: any) {
      LoggerService.error('Failed to load safekeeping', err);
      enqueueSnackbar('Failed to load safekeeping data', { variant: 'error' });
    } finally {
      setSafekeepingLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (tabIndex === 1) loadSafekeeping();
  }, [tabIndex, loadSafekeeping]);

  // NAV verification data loading
  const loadNavVerifications = useCallback(async () => {
    setNavLoading(true);
    try {
      const data = await depositaryService.listNavVerifications();
      setNavItems(data);
    } catch (err: any) {
      LoggerService.error('Failed to load NAV verifications', err);
      enqueueSnackbar('Failed to load NAV verification data', { variant: 'error' });
    } finally {
      setNavLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (tabIndex === 2) loadNavVerifications();
  }, [tabIndex, loadNavVerifications]);

  // Reports data loading
  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const data = await depositaryService.listReports();
      setReports(data);
    } catch (err: any) {
      LoggerService.error('Failed to load reports', err);
      enqueueSnackbar('Failed to load depositary reports', { variant: 'error' });
    } finally {
      setReportsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (tabIndex === 3) loadReports();
  }, [tabIndex, loadReports]);

  // Cash actions
  const handleConfirmCash = async (id: string) => {
    setActionLoading(id);
    try {
      const updated = await depositaryService.confirmCash(id);
      setCashItems((prev) => prev.map((c) => (c.id === id ? updated : c)));
      enqueueSnackbar('Cash movement confirmed', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to confirm cash', err);
      enqueueSnackbar(err.message || 'Failed to confirm', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleFlagCash = async () => {
    if (!flagTarget || !flagReason.trim()) return;
    setActionLoading(flagTarget);
    try {
      const updated = await depositaryService.flagCash(flagTarget, flagReason);
      setCashItems((prev) => prev.map((c) => (c.id === flagTarget ? updated : c)));
      enqueueSnackbar('Cash movement flagged', { variant: 'warning' });
      setFlagDialogOpen(false);
      setFlagReason('');
      setFlagTarget(null);
    } catch (err: any) {
      LoggerService.error('Failed to flag cash', err);
      enqueueSnackbar(err.message || 'Failed to flag', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  // Safekeeping actions
  const handleTriggerReconciliation = async () => {
    if (!reconFundId) {
      enqueueSnackbar('Please select a fund', { variant: 'warning' });
      return;
    }
    setSafekeepingLoading(true);
    try {
      const results = await depositaryService.triggerReconciliation(reconFundId, reconDate);
      setSafekeepingItems(results);
      enqueueSnackbar('Reconciliation completed', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Reconciliation failed', err);
      enqueueSnackbar(err.message || 'Reconciliation failed', { variant: 'error' });
    } finally {
      setSafekeepingLoading(false);
    }
  };

  const handleResolveBreak = async () => {
    if (!resolveTarget || !resolveNotes.trim()) return;
    setActionLoading(resolveTarget);
    try {
      const updated = await depositaryService.resolveBreak(resolveTarget, resolveNotes);
      setSafekeepingItems((prev) => prev.map((s) => (s.id === resolveTarget ? updated : s)));
      enqueueSnackbar('Break resolved', { variant: 'success' });
      setResolveDialogOpen(false);
      setResolveNotes('');
      setResolveTarget(null);
    } catch (err: any) {
      LoggerService.error('Failed to resolve break', err);
      enqueueSnackbar(err.message || 'Failed to resolve', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  // NAV actions
  const handleVerifyNav = async () => {
    if (!verifyTarget || !depositaryNav) return;
    setActionLoading(verifyTarget);
    try {
      const updated = await depositaryService.verifyNav(verifyTarget, parseFloat(depositaryNav));
      setNavItems((prev) => prev.map((n) => (n.id === verifyTarget ? updated : n)));
      enqueueSnackbar('NAV verified', { variant: 'success' });
      setVerifyDialogOpen(false);
      setDepositaryNav('');
      setVerifyTarget(null);
    } catch (err: any) {
      LoggerService.error('Failed to verify NAV', err);
      enqueueSnackbar(err.message || 'Failed to verify', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisputeNav = async () => {
    if (!disputeTarget || !disputeReason.trim()) return;
    setActionLoading(disputeTarget);
    try {
      const updated = await depositaryService.disputeNav(disputeTarget, disputeReason);
      setNavItems((prev) => prev.map((n) => (n.id === disputeTarget ? updated : n)));
      enqueueSnackbar('NAV dispute submitted', { variant: 'info' });
      setDisputeDialogOpen(false);
      setDisputeReason('');
      setDisputeTarget(null);
    } catch (err: any) {
      LoggerService.error('Failed to dispute NAV', err);
      enqueueSnackbar(err.message || 'Failed to dispute', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  // Report actions
  const handleGenerateReport = async () => {
    if (!genReportFundId || !genReportType || !genReportPeriod) {
      enqueueSnackbar('Please fill all report fields', { variant: 'warning' });
      return;
    }
    setReportsLoading(true);
    try {
      const report = await depositaryService.generateReport({
        fund_id: genReportFundId,
        report_type: genReportType,
        report_period: genReportPeriod,
      });
      setReports((prev) => [report, ...prev]);
      enqueueSnackbar('Report generated', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to generate report', err);
      enqueueSnackbar(err.message || 'Failed to generate report', { variant: 'error' });
    } finally {
      setReportsLoading(false);
    }
  };

  const handleApproveReport = async (id: string) => {
    setActionLoading(id);
    try {
      const updated = await depositaryService.approveReport(id);
      setReports((prev) => prev.map((r) => (r.id === id ? updated : r)));
      enqueueSnackbar('Report approved', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to approve report', err);
      enqueueSnackbar(err.message || 'Failed to approve', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitReport = async (id: string) => {
    setActionLoading(id);
    try {
      const updated = await depositaryService.submitReport(id);
      setReports((prev) => prev.map((r) => (r.id === id ? updated : r)));
      enqueueSnackbar('Report submitted', { variant: 'success' });
    } catch (err: any) {
      LoggerService.error('Failed to submit report', err);
      enqueueSnackbar(err.message || 'Failed to submit', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusChip = (status: string) => {
    const map: Record<string, { color: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary'; label: string }> = {
      pending: { color: 'default', label: 'Pending' },
      confirmed: { color: 'success', label: 'Confirmed' },
      flagged: { color: 'error', label: 'Flagged' },
      matched: { color: 'success', label: 'Matched' },
      break: { color: 'error', label: 'Break' },
      resolved: { color: 'info', label: 'Resolved' },
      submitted: { color: 'info', label: 'Submitted' },
      verified: { color: 'success', label: 'Verified' },
      disputed: { color: 'warning', label: 'Disputed' },
      draft: { color: 'default', label: 'Draft' },
      approved: { color: 'primary', label: 'Approved' },
      generated: { color: 'info', label: 'Generated' },
    };
    const info = map[status] || { color: 'default' as const, label: status };
    return <Chip size="small" label={info.label} color={info.color} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={() => { setError(null); loadFunds(); }}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Depositary Oversight</Typography>
        <Typography variant="body2" color="text.secondary">
          Cash monitoring, safekeeping reconciliation, NAV verification, and regulatory reporting
        </Typography>
      </Box>

      <Card>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<AccountBalance />} iconPosition="start" label="Cash Monitoring" />
          <Tab icon={<CompareArrows />} iconPosition="start" label="Safekeeping" />
          <Tab icon={<VerifiedUser />} iconPosition="start" label="NAV Verification" />
          <Tab icon={<Summarize />} iconPosition="start" label="Reports" />
        </Tabs>

        {/* Cash Monitoring Tab */}
        <TabPanel value={tabIndex} index={0}>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Fund</InputLabel>
                  <Select value={cashFundFilter} onChange={(e) => setCashFundFilter(e.target.value)} label="Filter by Fund">
                    <MenuItem value="">All Funds</MenuItem>
                    {funds.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Status</InputLabel>
                  <Select value={cashStatusFilter} onChange={(e) => setCashStatusFilter(e.target.value)} label="Filter by Status">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="flagged">Flagged</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="outlined" startIcon={<Refresh />} onClick={loadCashItems} disabled={cashLoading} fullWidth>
                  Refresh
                </Button>
              </Grid>
            </Grid>

            {cashLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : cashItems.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No cash movements found
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fund</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Counterparty</TableCell>
                      <TableCell>Value Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashItems.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.fund_name}</TableCell>
                        <TableCell>{item.account_name}</TableCell>
                        <TableCell>{item.movement_type}</TableCell>
                        <TableCell align="right">{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell>{item.currency}</TableCell>
                        <TableCell>{item.counterparty}</TableCell>
                        <TableCell>{new Date(item.value_date).toLocaleDateString()}</TableCell>
                        <TableCell>{getStatusChip(item.status)}</TableCell>
                        <TableCell align="right">
                          {item.status === 'pending' && (
                            <>
                              <Tooltip title="Confirm">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleConfirmCash(item.id)}
                                  disabled={actionLoading === item.id}
                                >
                                  {actionLoading === item.id ? <CircularProgress size={16} /> : <CheckCircle />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Flag">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => { setFlagTarget(item.id); setFlagDialogOpen(true); }}
                                >
                                  <Flag />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>

        {/* Safekeeping Tab */}
        <TabPanel value={tabIndex} index={1}>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fund</InputLabel>
                  <Select value={reconFundId} onChange={(e) => setReconFundId(e.target.value)} label="Fund">
                    {funds.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Reconciliation Date"
                  value={reconDate}
                  onChange={(e) => setReconDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  startIcon={safekeepingLoading ? <CircularProgress size={16} /> : <CompareArrows />}
                  onClick={handleTriggerReconciliation}
                  disabled={safekeepingLoading || !reconFundId}
                  fullWidth
                >
                  Trigger Reconciliation
                </Button>
              </Grid>
            </Grid>

            {safekeepingLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : safekeepingItems.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No safekeeping records found. Trigger a reconciliation to start.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fund</TableCell>
                      <TableCell>Asset</TableCell>
                      <TableCell>ISIN</TableCell>
                      <TableCell align="right">Fund Qty</TableCell>
                      <TableCell align="right">Custodian Qty</TableCell>
                      <TableCell align="right">Difference</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safekeepingItems.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.fund_name}</TableCell>
                        <TableCell>{item.asset_name}</TableCell>
                        <TableCell>{item.isin}</TableCell>
                        <TableCell align="right">{item.quantity_fund.toLocaleString()}</TableCell>
                        <TableCell align="right">{item.quantity_custodian.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: item.difference !== 0 ? 'error.main' : 'success.main' }}>
                          {item.difference.toLocaleString()}
                        </TableCell>
                        <TableCell>{getStatusChip(item.status)}</TableCell>
                        <TableCell>{new Date(item.reconciliation_date).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          {item.status === 'break' && (
                            <Tooltip title="Resolve Break">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => { setResolveTarget(item.id); setResolveDialogOpen(true); }}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>

        {/* NAV Verification Tab */}
        <TabPanel value={tabIndex} index={2}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="outlined" startIcon={<Refresh />} onClick={loadNavVerifications} disabled={navLoading}>
                Refresh
              </Button>
            </Box>

            {navLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : navItems.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No NAV verifications pending
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fund</TableCell>
                      <TableCell>NAV Date</TableCell>
                      <TableCell align="right">Fund NAV</TableCell>
                      <TableCell align="right">Depositary NAV</TableCell>
                      <TableCell align="right">Diff %</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {navItems.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.fund_name}</TableCell>
                        <TableCell>{new Date(item.nav_date).toLocaleDateString()}</TableCell>
                        <TableCell align="right">{item.fund_nav.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell align="right">
                          {item.depositary_nav != null
                            ? item.depositary_nav.toLocaleString(undefined, { minimumFractionDigits: 2 })
                            : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{
                          color: item.difference_pct != null && Math.abs(item.difference_pct) > 0.5
                            ? 'error.main'
                            : item.difference_pct != null && Math.abs(item.difference_pct) > 0.1
                            ? 'warning.main'
                            : 'text.primary',
                        }}>
                          {item.difference_pct != null ? `${item.difference_pct.toFixed(4)}%` : '-'}
                        </TableCell>
                        <TableCell>{getStatusChip(item.status)}</TableCell>
                        <TableCell align="right">
                          {item.status === 'submitted' && (
                            <>
                              <Tooltip title="Verify">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => { setVerifyTarget(item.id); setVerifyDialogOpen(true); }}
                                >
                                  <VerifiedUser />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Dispute">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => { setDisputeTarget(item.id); setDisputeDialogOpen(true); }}
                                >
                                  <Warning />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={tabIndex} index={3}>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fund</InputLabel>
                  <Select value={genReportFundId} onChange={(e) => setGenReportFundId(e.target.value)} label="Fund">
                    {funds.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Report Type</InputLabel>
                  <Select value={genReportType} onChange={(e) => setGenReportType(e.target.value)} label="Report Type">
                    <MenuItem value="annual">Annual</MenuItem>
                    <MenuItem value="semi-annual">Semi-Annual</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="ad-hoc">Ad-hoc</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Report Period"
                  placeholder="e.g. 2026-Q1"
                  value={genReportPeriod}
                  onChange={(e) => setGenReportPeriod(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  startIcon={reportsLoading ? <CircularProgress size={16} /> : <Summarize />}
                  onClick={handleGenerateReport}
                  disabled={reportsLoading || !genReportFundId || !genReportType || !genReportPeriod}
                  fullWidth
                >
                  Generate Report
                </Button>
              </Grid>
            </Grid>

            {reportsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : reports.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No depositary reports found
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fund</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Generated</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report.id} hover>
                        <TableCell>{report.fund_name}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{report.report_type}</TableCell>
                        <TableCell>{report.report_period}</TableCell>
                        <TableCell>{new Date(report.generated_at).toLocaleDateString()}</TableCell>
                        <TableCell>{getStatusChip(report.status)}</TableCell>
                        <TableCell align="right">
                          {report.status === 'generated' && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleApproveReport(report.id)}
                                disabled={actionLoading === report.id}
                              >
                                {actionLoading === report.id ? <CircularProgress size={16} /> : <ThumbUp />}
                              </IconButton>
                            </Tooltip>
                          )}
                          {report.status === 'approved' && (
                            <Tooltip title="Submit to Regulator">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleSubmitReport(report.id)}
                                disabled={actionLoading === report.id}
                              >
                                {actionLoading === report.id ? <CircularProgress size={16} /> : <Send />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </TabPanel>
      </Card>

      {/* Flag Cash Dialog */}
      <Dialog open={flagDialogOpen} onClose={() => setFlagDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Flag Cash Movement
          <IconButton onClick={() => setFlagDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Flagging"
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleFlagCash}
            disabled={!flagReason.trim() || actionLoading === flagTarget}
          >
            Flag
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Break Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Resolve Position Break
          <IconButton onClick={() => setResolveDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Resolution Notes"
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResolveBreak}
            disabled={!resolveNotes.trim() || actionLoading === resolveTarget}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify NAV Dialog */}
      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Verify NAV
          <IconButton onClick={() => setVerifyDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Depositary NAV"
            value={depositaryNav}
            onChange={(e) => setDepositaryNav(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleVerifyNav}
            disabled={!depositaryNav || actionLoading === verifyTarget}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispute NAV Dialog */}
      <Dialog open={disputeDialogOpen} onClose={() => setDisputeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Dispute NAV
          <IconButton onClick={() => setDisputeDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Dispute Reason"
            value={disputeReason}
            onChange={(e) => setDisputeReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisputeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDisputeNav}
            disabled={!disputeReason.trim() || actionLoading === disputeTarget}
          >
            Submit Dispute
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepositaryPage;
